import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import { normalizeGridSpec, type CharacterGridSpec } from "@/character/grid";
import type { CharacterProfile } from "@/character/storage";

/** Stay under Firestore's 1 MiB document cap (proto overhead included). */
const CLOUD_DOC_BUDGET = 900_000;

function utf8Size(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function envelopeSize(payload: unknown): number {
  return utf8Size({ savedAt: 0, payload });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดภาพตัวละครไม่สำเร็จ"));
    img.src = src;
  });
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Plain Firestore fields only. Drops extra keys that can be nested arrays or host objects. */
export function pickCharacterCloudProfile(profile: CharacterProfile): CharacterProfile {
  const grid = normalizeGridSpec(profile.grid);
  const actions = profile.actions ?? ({} as CharacterProfile["actions"]);
  const idle = typeof actions.idle === "string" ? actions.idle : "";
  return {
    version: 2,
    name: typeof profile.name === "string" ? profile.name : "",
    createdAt: finiteNumber(profile.createdAt, Date.now()),
    spritesheetDataUrl:
      typeof profile.spritesheetDataUrl === "string" ? profile.spritesheetDataUrl : "",
    grid: {
      columns: finiteNumber(grid.columns, 6),
      rows: finiteNumber(grid.rows, 4),
      frameWidth: finiteNumber(grid.frameWidth, 96),
      frameHeight: finiteNumber(grid.frameHeight, 96),
      regionX: finiteNumber(grid.regionX, 0),
      regionY: finiteNumber(grid.regionY, 0),
      sheetColumns: finiteNumber(grid.sheetColumns, 6),
      alignX: finiteNumber(grid.alignX, 0),
      alignY: finiteNumber(grid.alignY, 0),
    },
    actions: {
      idle,
      run: typeof actions.run === "string" ? actions.run : idle,
      sit: typeof actions.sit === "string" ? actions.sit : idle,
      attack: typeof actions.attack === "string" ? actions.attack : idle,
    },
  };
}

function scaleGrid(grid: CharacterGridSpec, factor: number): CharacterGridSpec {
  const n = (value: number) => Math.max(0, Math.round(value * factor));
  return {
    ...grid,
    frameWidth: Math.max(1, n(grid.frameWidth)),
    frameHeight: Math.max(1, n(grid.frameHeight)),
    regionX: n(grid.regionX),
    regionY: n(grid.regionY),
    alignX: Math.round(grid.alignX * factor),
    alignY: Math.round(grid.alignY * factor),
  };
}

async function scaleImageDataUrl(
  dataUrl: string,
  scale: number,
): Promise<{ dataUrl: string; width: number; height: number }> {
  const img = await loadImage(dataUrl);
  const width = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
  const height = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("ย่อภาพตัวละครไม่สำเร็จ");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0, width, height);
  return { dataUrl: canvas.toDataURL("image/png"), width, height };
}

async function actionPreviews(
  sheetUrl: string,
  grid: CharacterGridSpec,
): Promise<Record<CharacterActionKey, string>> {
  const img = await loadImage(sheetUrl);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("ย่อภาพตัวละครไม่สำเร็จ");
  ctx.imageSmoothingEnabled = false;
  const actions = {} as Record<CharacterActionKey, string>;
  for (const action of CHARACTER_ACTIONS) {
    const sw = Math.max(1, grid.frameWidth);
    const sh = Math.max(1, grid.frameHeight);
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      img,
      grid.regionX + grid.alignX,
      grid.regionY + grid.alignY + action.row * grid.frameHeight,
      sw,
      sh,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    actions[action.key] = canvas.toDataURL("image/png");
  }
  return actions;
}

/**
 * Character cloud docs store the sheet plus four frame previews.
 * Firestore rejects an oversized `payload` as "invalid nested entity".
 * Profiles already under the cap are returned unchanged.
 */
export async function fitCharacterProfileForCloud(
  profile: CharacterProfile,
): Promise<CharacterProfile> {
  const picked = pickCharacterCloudProfile(profile);
  if (!picked.spritesheetDataUrl || envelopeSize(picked) <= CLOUD_DOC_BUDGET) {
    return picked;
  }

  const source = await loadImage(picked.spritesheetDataUrl);
  const srcW = source.naturalWidth || 1;
  let scale = Math.min(1, Math.sqrt((CLOUD_DOC_BUDGET * 0.55) / envelopeSize(picked)));
  let last = picked;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const scaled = await scaleImageDataUrl(picked.spritesheetDataUrl, scale);
    const grid = scaleGrid(picked.grid, scaled.width / srcW);
    const actions = await actionPreviews(scaled.dataUrl, grid);
    last = {
      ...picked,
      spritesheetDataUrl: scaled.dataUrl,
      grid,
      actions,
    };
    if (envelopeSize(last) <= CLOUD_DOC_BUDGET) return last;
    scale *= 0.72;
  }

  if (envelopeSize(last) <= CLOUD_DOC_BUDGET) return last;
  throw new Error("ภาพตัวละครใหญ่เกินที่คลาวด์รับได้");
}
